import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { ReactNode, useEffect, useId, useRef } from 'react';
import type { SidebarTab } from './types';
import { useTranslation } from '../../i18n/useTranslation';

interface FloatingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
}

export function FloatingPanel({ isOpen, onClose, title, children, width = '384px' }: FloatingPanelProps) {
  const { t } = useTranslation();
  const titleId = `floating-panel-title-${useId()}`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  if (previousFocusRef.current === null && typeof document !== 'undefined') {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
  }
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.focus();
    return () => {
      requestAnimationFrame(() => {
        if (!dialog?.isConnected && previousFocusRef.current) {
          previousFocusRef.current.focus?.();
        }
      });
    };
  }, []);
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-40"
          />

          {/* Floating Panel */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
            initial={{ x: '100%', opacity: 0, scale: 0.95 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: '100%', opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 260 }}
            className="fixed right-24 top-20 z-50
                     bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl
                     border border-neutral-200/60
                     flex flex-col overflow-hidden"
            style={{ width, maxHeight: 'calc(100vh - 160px)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-neutral-200/60 bg-gradient-to-r from-neutral-50/80 to-white/50">
              <h3 id={titleId} className="text-base font-semibold text-neutral-900">{title}</h3>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="w-8 h-8 rounded-lg flex items-center justify-center
                         bg-neutral-100/80 hover:bg-neutral-200/80 transition-colors duration-200
                         focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                aria-label={t('App.Reader.FloatingPanel.CloseLabel')}
              >
                <X className="w-4 h-4 text-neutral-600" aria-hidden="true" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Mobile version - slides up from bottom
export function MobileFloatingPanel({ isOpen, onClose, title, children }: Omit<FloatingPanelProps, 'width'>) {
  const { t } = useTranslation();
  const titleId = `floating-panel-title-${useId()}`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  if (previousFocusRef.current === null && typeof document !== 'undefined') {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
  }
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.focus();
    return () => {
      requestAnimationFrame(() => {
        if (!dialog?.isConnected && previousFocusRef.current) {
          previousFocusRef.current.focus?.();
        }
      });
    };
  }, []);
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[1099] md:hidden"
          />

          {/* Bottom Sheet Panel */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
            initial={{ y: '100%', scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: '100%', scale: 0.95 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed left-0 right-0 bottom-0 md:hidden
                     bg-white/95 backdrop-blur-xl rounded-t-[32px] shadow-2xl
                     border-t border-x border-neutral-200/60
                     flex flex-col overflow-hidden"
            style={{
              maxHeight: 'calc(100dvh - 60px)',
              height: '85dvh',
              zIndex: 1100,
            }}
          >
            {/* Drag Handle — only this area is draggable to dismiss */}
            <motion.div
              className="flex justify-center pt-2 pb-1.5 cursor-grab active:cursor-grabbing"
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 100) onClose();
              }}
            >
              <div className="w-10 h-1 bg-neutral-300 rounded-full" />
            </motion.div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-2.5 border-b border-neutral-200/60 bg-gradient-to-r from-neutral-50/80 to-white/50">
              <h3 id={titleId} className="text-base font-semibold text-neutral-900">{title}</h3>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-9 h-9 rounded-full flex items-center justify-center
                         bg-neutral-100/80 hover:bg-neutral-200/80 transition-colors duration-200"
                aria-label={t('App.Reader.FloatingPanel.CloseLabel')}
              >
                <X className="w-4 h-4 text-neutral-600" aria-hidden="true" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
